import { billService } from "../services/billService";
import { appStore } from "../state/store";
import { can } from "../state/session";
import { createOverlay } from "./overlay";
import { createStepController } from "./steps";
import { byId, qsAll } from "./dom";
import { formatDateShort, formatNaira, toKobo } from "./format";
import type { Bill } from "../types";

export interface BillCentreController {
  open(): void;
  close(): void;
}

export function initBillCentre(onViewTransaction: () => void): BillCentreController {
  const overlay = createOverlay("sheet-bill-centre");
  const root = byId("bill-centre-flow");
  const steps = createStepController(root);

  const listEl = byId("bill-list");
  const filterBtns = qsAll<HTMLButtonElement>("[data-bill-filter]", root);
  const addBillBtn = byId<HTMLButtonElement>("bill-add-btn");

  const reviewTitle = byId("bill-review-title");
  const reviewCategory = byId("bill-review-category");
  const reviewAmount = byId("bill-review-amount");
  const reviewDue = byId("bill-review-due");
  const payBtn = byId<HTMLButtonElement>("bill-pay-btn");
  const backBtn = byId("bill-back-btn");

  const successTitle = byId("bill-success-title");
  const successReference = byId("bill-success-reference");
  const viewTxBtn = byId("bill-view-tx-btn");
  const doneBtn = byId("bill-success-done-btn");

  const failureReason = byId("bill-failure-reason");
  const retryBtn = byId("bill-retry-btn");
  const cancelBtn = byId("bill-cancel-btn");

  const addTitleInput = byId<HTMLInputElement>("bill-add-title");
  const addCategoryInput = byId<HTMLInputElement>("bill-add-category");
  const addAmountInput = byId<HTMLInputElement>("bill-add-amount");
  const addDueInput = byId<HTMLInputElement>("bill-add-due");
  const addError = byId("bill-add-error");
  const addSubmitBtn = byId<HTMLButtonElement>("bill-add-submit-btn");
  const addCancelBtn = byId("bill-add-cancel-btn");

  let selectedBill: Bill | null = null;
  let currentFilter: "outstanding" | "paid" = "outstanding";

  function renderList(): void {
    const bills = appStore.state.bills.filter((b) =>
      currentFilter === "outstanding" ? b.status !== "paid" : b.status === "paid"
    );
    listEl.innerHTML = "";

    if (bills.length === 0) {
      const empty = document.createElement("li");
      const loading = appStore.state.loading.bills;
      empty.className = loading ? "bill-empty bill-empty--loading" : "bill-empty";
      empty.textContent = loading
        ? "Loading\u2026"
        : currentFilter === "outstanding"
          ? "No outstanding bills."
          : "No paid bills yet.";
      listEl.appendChild(empty);
      return;
    }

    bills.forEach((bill) => {
      const row = document.createElement("li");
      row.className = "bill-row";
      row.innerHTML = `
        <div class="bill-row-main">
          <div class="bill-row-title">${bill.title}</div>
          <div class="bill-row-meta">${bill.category} \u00b7 Due ${formatDateShort(bill.dueDate)}</div>
        </div>
        <div class="bill-row-right">
          <div class="bill-row-amount">\u20a6${formatNaira(bill.amountKobo)}</div>
          <span class="bill-status-badge bill-status-badge--${bill.status}">${bill.status}</span>
        </div>
      `;
      if (bill.status !== "paid") {
        row.addEventListener("click", () => openReview(bill));
      }
      listEl.appendChild(row);
    });
  }

  appStore.subscribe(() => {
    if (steps && !root.hidden) renderList();
  });

  filterBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      currentFilter = btn.dataset.billFilter as "outstanding" | "paid";
      filterBtns.forEach((b) => b.classList.toggle("is-active", b === btn));
      renderList();
    });
  });

  function openReview(bill: Bill): void {
    selectedBill = bill;
    reviewTitle.textContent = bill.title;
    reviewCategory.textContent = bill.category;
    reviewAmount.textContent = `\u20a6${formatNaira(bill.amountKobo)}`;
    reviewDue.textContent = formatDateShort(bill.dueDate);
    steps.show("review");
  }

  backBtn.addEventListener("click", () => steps.show("list"));
  cancelBtn.addEventListener("click", () => steps.show("list"));

  payBtn.addEventListener("click", async () => {
    if (!selectedBill) return;
    steps.show("pending");
    try {
      const result = await billService.payBill(selectedBill.id);
      if (result.status === "success") {
        const wallet = appStore.state.wallet;
        if (wallet) {
          appStore.setWallet({ ...wallet, balanceKobo: wallet.balanceKobo - result.amountKobo, updatedAt: new Date().toISOString() });
        }
        appStore.updateBill({ ...selectedBill, status: "paid", paidAt: new Date().toISOString() });
        appStore.prependTransaction({
          id: result.reference,
          type: "bill_payment",
          status: "success",
          amountKobo: result.amountKobo,
          direction: "debit",
          description: selectedBill.title,
          reference: result.reference,
          createdAt: new Date().toISOString(),
          billId: selectedBill.id,
        });
        successTitle.textContent = selectedBill.title;
        successReference.textContent = result.reference;
        steps.show("success");
      } else {
        failureReason.textContent = result.failureReason ?? "This payment could not be completed.";
        steps.show("failed");
      }
    } catch {
      failureReason.textContent = "Something went wrong. Please try again.";
      steps.show("failed");
    }
  });

  retryBtn.addEventListener("click", () => steps.show("review"));

  viewTxBtn.addEventListener("click", () => {
    overlay.close();
    onViewTransaction();
  });
  doneBtn.addEventListener("click", () => {
    steps.show("list");
    overlay.close();
  });

  /* -------------------------- Add Bill (admin only) -------------------------- */

  function resetAddForm(): void {
    addTitleInput.value = "";
    addCategoryInput.value = "";
    addAmountInput.value = "";
    addDueInput.value = "";
    addError.hidden = true;
  }

  addBillBtn.addEventListener("click", () => {
    resetAddForm();
    steps.show("add");
    addTitleInput.focus();
  });

  addCancelBtn.addEventListener("click", () => steps.show("list"));

  addSubmitBtn.addEventListener("click", async () => {
    const title = addTitleInput.value.trim();
    const category = addCategoryInput.value.trim();
    const naira = Number(addAmountInput.value);
    const dueDate = addDueInput.value;

    if (!title || !category || !naira || naira <= 0 || !dueDate) {
      addError.textContent = "Fill in every field with a valid amount and due date.";
      addError.hidden = false;
      return;
    }
    addError.hidden = true;
    addSubmitBtn.disabled = true;
    addSubmitBtn.textContent = "Saving\u2026";

    try {
      const bill = await billService.createBill({ title, category, amountKobo: toKobo(naira), dueDate });
      appStore.addBill(bill);
      currentFilter = "outstanding";
      filterBtns.forEach((b) => b.classList.toggle("is-active", b.dataset.billFilter === "outstanding"));
      steps.show("list");
    } catch (err) {
      addError.textContent = err instanceof Error ? err.message : "Couldn't save this bill. Please try again.";
      addError.hidden = false;
    } finally {
      addSubmitBtn.disabled = false;
      addSubmitBtn.textContent = "Save Bill";
    }
  });

  function open(): void {
    addBillBtn.hidden = !can("bills:create");
    steps.show("list");
    renderList();
    overlay.open();
  }

  return { open, close: overlay.close };
}
